import { sort } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { toURL } from "@prismicio/editor-support/URL"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"
import { languageOkType } from "./document"

const repositoryAuthor = z.object({
  id: z.string(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  email: z.string()
})

const repositoryAuthors = z.array(repositoryAuthor)

export type RepositoryAuthor = z.TypeOf<typeof repositoryAuthor>

const repositoryOkType = z
  .object({
    simulator_url: z.string().url().nullish(),
    languages: z
      .array(languageOkType)
      .transform((arr) => sort(arr, (lang) => lang.name)),
    authors: repositoryAuthors.optional().default([])
  })
  .transform(({ simulator_url, ...rest }) => {
    const simulatorUrl =
      simulator_url && isValidSimulatorURL(simulator_url)
        ? simulator_url
        : undefined

    return {
      ...rest,
      simulatorUrl
    }
  })

export type Repository = z.TypeOf<typeof repositoryOkType>

export function getRepository(
  baseUrl: URL,
  authStrategy: AuthStrategy
): Promise<Repository> {
  return request(new URL("repository", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositoryOkType
  })
}

export function isValidSimulatorURL(urlString: string) {
  const url = toURL(urlString)
  if (!url) return false
  return url.pathname.endsWith("/slice-simulator")
}

const repositoryTagsResponse = z.object({
  results: z.array(z.string())
})

type RepositoryTags = z.TypeOf<typeof repositoryTagsResponse>

export function getRepositoryTags(
  baseUrl: URL,
  authStrategy: AuthStrategy
): Promise<RepositoryTags> {
  return request(new URL("repository/tags", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositoryTagsResponse
  })
}
